/**
 * AdmissionFormBuilder — drag-reorder + add/edit/remove custom fields.
 *
 * Used inside the School module Admissions tab. Each tenant can create
 * multiple form schemas (a default + per-class variants). The public
 * /apply page renders these dynamic fields above the system core fields.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, Trash2, ArrowUp, ArrowDown, FileText, GripVertical,
  Edit3, Loader2, Star, ShieldCheck,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Yes / No' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'file', label: 'File upload' },
];

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const EMPTY_FORM = {
  name: '', description: '', fields: [],
  applies_to_class_ids: [], is_active: true, is_default: false,
};

const EMPTY_FIELD = {
  key: '', label: '', type: 'text', required: false,
  placeholder: '', helptext: '', options: [], order: 0,
};

export default function AdmissionFormBuilder({ tenantId }) {
  const qc = useQueryClient();
  const [editingForm, setEditingForm] = useState(null);

  const { data: forms = [], isLoading } = useQuery({
    queryKey: ['admission-forms', tenantId],
    queryFn: () => api(`/api/school/tenants/${tenantId}/admission-forms`),
    enabled: !!tenantId,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/api/school/tenants/${tenantId}/classes`),
    enabled: !!tenantId,
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api(`/api/school/tenants/${tenantId}/admission-forms/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Form deleted');
      qc.invalidateQueries({ queryKey: ['admission-forms', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="admission-form-builder">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" /> Admission Form Builder
          </h3>
          <p className="text-xs text-slate-500">
            Design custom fields parents fill in on the public application page. Create one default and (optional) per-class variants.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditingForm({ ...EMPTY_FORM, _isNew: true })} data-testid="admission-form-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New form
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      ) : forms.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-slate-500">
            No custom forms yet. The default application form is used. Click <b>New form</b> to start designing your own.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {forms.map((f) => (
            <Card key={f.id} className="hover:shadow-md transition-shadow" data-testid={`admission-form-card-${f.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {f.name}
                      {f.is_default && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50"><Star className="h-3 w-3 mr-0.5" />Default</Badge>}
                      {!f.is_active && <Badge variant="outline" className="text-[10px] text-slate-500">Inactive</Badge>}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">{(f.fields || []).length} field{(f.fields || []).length === 1 ? '' : 's'}{(f.applies_to_class_ids || []).length > 0 ? ` · ${f.applies_to_class_ids.length} class${f.applies_to_class_ids.length === 1 ? '' : 'es'}` : ' · All classes'}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingForm(f)} data-testid={`admission-form-edit-${f.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => { if (window.confirm(`Delete "${f.name}"?`)) deleteMut.mutate(f.id); }} data-testid={`admission-form-delete-${f.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {f.description && <p className="text-xs text-slate-600 mb-2 line-clamp-2">{f.description}</p>}
                <div className="flex flex-wrap gap-1">
                  {(f.fields || []).slice(0, 6).map((fld) => (
                    <Badge key={fld.key} variant="secondary" className="text-[10px]">
                      {fld.label}{fld.required && <ShieldCheck className="h-2.5 w-2.5 ml-0.5 text-rose-500" />}
                    </Badge>
                  ))}
                  {(f.fields || []).length > 6 && (
                    <Badge variant="outline" className="text-[10px]">+{f.fields.length - 6} more</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editingForm && (
        <FormEditorDialog
          tenantId={tenantId}
          classes={classes}
          form={editingForm}
          onClose={() => setEditingForm(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admission-forms', tenantId] });
            setEditingForm(null);
          }}
        />
      )}
    </div>
  );
}


function FormEditorDialog({ tenantId, classes, form, onClose, onSaved }) {
  const isNew = !!form._isNew;
  const [state, setState] = useState({
    name: form.name || '',
    description: form.description || '',
    fields: form.fields ? form.fields.map((f) => ({ ...EMPTY_FIELD, ...f })) : [],
    applies_to_class_ids: form.applies_to_class_ids || [],
    is_active: form.is_active !== false,
    is_default: !!form.is_default,
  });

  const saveMut = useMutation({
    mutationFn: (payload) => api(
      isNew
        ? `/api/school/tenants/${tenantId}/admission-forms`
        : `/api/school/tenants/${tenantId}/admission-forms/${form.id}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(payload) },
    ),
    onSuccess: () => {
      toast.success(isNew ? 'Form created' : 'Form updated');
      onSaved?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const addField = () => {
    setState((s) => ({ ...s, fields: [...s.fields, { ...EMPTY_FIELD, label: `Field ${s.fields.length + 1}`, order: s.fields.length }] }));
  };
  const updateField = (idx, patch) => {
    setState((s) => ({ ...s, fields: s.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)) }));
  };
  const removeField = (idx) => {
    setState((s) => ({ ...s, fields: s.fields.filter((_, i) => i !== idx) }));
  };
  const move = (idx, dir) => {
    setState((s) => {
      const next = [...s.fields];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return s;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...s, fields: next.map((f, i) => ({ ...f, order: i })) };
    });
  };
  const toggleClass = (cid) => {
    setState((s) => {
      const has = s.applies_to_class_ids.includes(cid);
      return {
        ...s,
        applies_to_class_ids: has
          ? s.applies_to_class_ids.filter((x) => x !== cid)
          : [...s.applies_to_class_ids, cid],
      };
    });
  };

  const submit = () => {
    if (!state.name.trim()) return toast.error('Name is required');
    saveMut.mutate({ ...state, fields: state.fields.map((f, i) => ({ ...f, order: i })) });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admission-form-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New admission form' : `Edit · ${form.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })}
                     placeholder="e.g. Primary Application Form" data-testid="admission-form-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={state.description} onChange={(e) => setState({ ...state, description: e.target.value })}
                     placeholder="What this form is used for" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={state.is_active} onCheckedChange={(v) => setState({ ...state, is_active: v })} data-testid="admission-form-active" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={state.is_default} onCheckedChange={(v) => setState({ ...state, is_default: v })} data-testid="admission-form-default" />
              <span>Default form <span className="text-xs text-slate-500">(used when no class match)</span></span>
            </label>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Applies to specific classes</Label>
            <p className="text-xs text-slate-500 mb-2">Leave empty to apply to every class. Tick the ones this form should be used for.</p>
            <div className="flex flex-wrap gap-1.5">
              {classes.length === 0 ? (
                <span className="text-xs text-slate-400">No classes defined yet.</span>
              ) : classes.map((c) => {
                const cid = c.id || c._id;
                const on = state.applies_to_class_ids.includes(cid);
                return (
                  <button key={cid} type="button"
                          onClick={() => toggleClass(cid)}
                          className={`px-2.5 py-1 text-xs rounded border ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                          data-testid={`admission-form-class-${cid}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold text-sm">Custom fields</Label>
              <Button size="sm" variant="outline" onClick={addField} data-testid="admission-form-add-field">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add field
              </Button>
            </div>
            {state.fields.length === 0 ? (
              <div className="text-center text-sm text-slate-400 border-2 border-dashed rounded-lg p-6">
                No custom fields yet. Click <b>Add field</b> to begin.
              </div>
            ) : (
              <div className="space-y-2">
                {state.fields.map((f, idx) => (
                  <FieldRow
                    key={idx}
                    field={f}
                    index={idx}
                    total={state.fields.length}
                    onChange={(patch) => updateField(idx, patch)}
                    onRemove={() => removeField(idx)}
                    onMove={(dir) => move(idx, dir)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saveMut.isPending} data-testid="admission-form-save">
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isNew ? 'Create form' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FieldRow({ field, index, total, onChange, onRemove, onMove }) {
  const needsOptions = field.type === 'select';
  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`admission-field-row-${index}`}>
      <div className="flex items-center gap-2 mb-2">
        <GripVertical className="h-4 w-4 text-slate-300" />
        <Input value={field.label}
               onChange={(e) => onChange({ label: e.target.value })}
               placeholder="Field label *"
               className="flex-1"
               data-testid={`admission-field-label-${index}`} />
        <Select value={field.type} onValueChange={(v) => onChange({ type: v })}>
          <SelectTrigger className="w-36" data-testid={`admission-field-type-${index}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1 text-xs whitespace-nowrap">
          <Switch checked={field.required} onCheckedChange={(v) => onChange({ required: v })} data-testid={`admission-field-required-${index}`} />
          Required
        </label>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={index === 0} onClick={() => onMove(-1)} title="Move up">
          <ArrowUp className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={index === total - 1} onClick={() => onMove(1)} title="Move down">
          <ArrowDown className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={onRemove} title="Remove field">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 ml-6">
        <Input value={field.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} placeholder="Placeholder text" className="text-xs" />
        <Input value={field.helptext || ''} onChange={(e) => onChange({ helptext: e.target.value })} placeholder="Help text (shown under field)" className="text-xs" />
      </div>
      {needsOptions && (
        <div className="ml-6 mt-2">
          <Input
            value={(field.options || []).join(', ')}
            onChange={(e) => onChange({ options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="Comma-separated options: Option A, Option B, Option C"
            className="text-xs"
            data-testid={`admission-field-options-${index}`}
          />
        </div>
      )}
    </div>
  );
}
